// mirror of auware/.../frontend/src/pages/auth/VerifyEmailPage.tsx (see frontend/CLAUDE.md mirror policy).
//
// Buyist adaptations: brand strings + logo swapped. Most imported users
// from FD will land here only via the email-change flow, since the FD
// import marks `email_verified_at=now()` at import time.

import {
  type FormEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";

import Button from "@/components/ui/Button";
import { useAuthStore } from "@/stores/auth";
import { usePageTitle } from "@/hooks/usePageTitle";
import api from "@/lib/api";

/**
 * 6-digit code entry page - shown after signup until email is verified.
 */
export function VerifyEmailPage(): JSX.Element {
  usePageTitle("Verify email");
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const login = useAuthStore((s) => s.login);
  const tokens = useAuthStore((s) => s.tokens);
  const logout = useAuthStore((s) => s.logout);

  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const [cooldown, setCooldown] = useState(30);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (user?.isEmailVerified) {
      navigate("/", { replace: true });
    }
  }, [user?.isEmailVerified, navigate]);

  useEffect(() => {
    if (cooldown <= 0) return;
    intervalRef.current = setInterval(() => {
      setCooldown((prev) => {
        if (prev <= 1) {
          if (intervalRef.current) clearInterval(intervalRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [cooldown]);

  const handleVerify = async (e: FormEvent) => {
    e.preventDefault();
    if (code.length !== 6) return;
    setError(null);
    setSuccess(null);
    setLoading(true);

    try {
      await api.post("/auth/verify-email", { code });

      if (user && tokens) {
        login({ ...user, isEmailVerified: true }, tokens);
      }

      navigate("/", { replace: true });
    } catch (err: unknown) {
      const message = (err as {
        response?: { data?: { detail?: string } };
      })?.response?.data?.detail ?? "Invalid code. Please try again.";
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  const handleResend = useCallback(async () => {
    if (cooldown > 0) return;
    setError(null);
    setSuccess(null);

    try {
      await api.post("/auth/verify-email/resend");
      setSuccess("A new code has been sent to your email.");
      setCooldown(30);
      setCode("");
    } catch (err: unknown) {
      const message = (err as {
        response?: { data?: { detail?: string } };
      })?.response?.data?.detail ?? "Could not resend code. Please try again.";
      setError(message);
    }
  }, [cooldown]);

  return (
    <div className="flex min-h-full items-center justify-center bg-neutral-50 px-4 py-12">
      <div className="w-full max-w-md space-y-8">
        <div className="text-center">
          <img
            src="/buyist-logo.png"
            alt="Buyist Support"
            className="mx-auto h-14 w-auto"
          />
          <h2 className="mt-2 text-xl font-semibold text-neutral-900">
            Let's get your email verified
          </h2>
          <p className="mt-1 text-sm text-neutral-500">
            To keep your account secure we need to verify your email address.
            Enter the code we have sent to{" "}
            <strong className="text-neutral-800">{user?.email}</strong> below.
          </p>
        </div>

        <form
          onSubmit={handleVerify}
          className="space-y-6 rounded-lg border border-neutral-200 bg-white p-6 shadow-sm"
        >
          {error && (
            <div className="rounded-md bg-red-50 px-4 py-3 text-sm text-red-700">
              {error}
            </div>
          )}
          {success && (
            <div className="rounded-md bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
              {success}
            </div>
          )}

          <div>
            <label
              htmlFor="verify-code"
              className="block text-sm font-medium text-neutral-700"
            >
              Code
            </label>
            <input
              id="verify-code"
              name="code"
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              required
              maxLength={6}
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
              className="mt-1 block w-full rounded-lg border-neutral-300 text-center text-2xl tracking-[0.3em] shadow-sm focus:border-primary-500 focus:ring-primary-500"
              placeholder="000000"
              autoFocus
            />
          </div>

          <div className="flex items-center gap-4">
            <Button type="submit" className="flex-1" isLoading={loading}>
              Verify
            </Button>
            {cooldown > 0 ? (
              <span className="whitespace-nowrap text-sm text-neutral-400">
                Resend code in {cooldown}
              </span>
            ) : (
              <button
                type="button"
                onClick={handleResend}
                className="whitespace-nowrap text-sm font-medium text-primary-600 hover:text-primary-500"
              >
                Resend code
              </button>
            )}
          </div>
        </form>

        <p className="text-center text-sm text-neutral-400">
          Wrong account?{" "}
          <button
            type="button"
            onClick={() => {
              logout();
              navigate("/login", { replace: true });
            }}
            className="font-medium text-primary-600 hover:text-primary-500"
          >
            Sign out
          </button>
        </p>
      </div>
    </div>
  );
}

export default VerifyEmailPage;
